import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Circle, FileText, BellRing, BellOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fmtDate } from '../../lib/dates';

// Expandable step-by-step progress for Peru / Ecuador bills
export const BillProgress = ({ country, boletin, followed, onFollow }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const id = boletin.replace(/[^\w-]/g, '_');
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (open) return setOpen(false);
    setOpen(true);
    if (data) return;
    setBusy(true);
    try { setData((await api.get(`/legislative/${country}/bills/${encodeURIComponent(boletin)}/progress`)).data); }
    catch { setData({ steps: [], error: true }); } finally { setBusy(false); }
  };
  const follow = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const r = (await api.post(`/legislative/${country}/bills/${encodeURIComponent(boletin)}/follow`)).data;
      onFollow(boletin, r.following);
      toast.success(r.following ? 'Te avisaremos en la campana cuando cambie de etapa' : 'Dejaste de seguir este proyecto');
    } catch { toast.error('No se pudo actualizar el seguimiento'); }
  };
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={toggle} data-testid={`bill-progress-btn-${id}`} className="rounded-full border border-amber-500/40 px-3 py-1 text-[11px] text-amber-300 hover:bg-amber-500/10 transition-colors">
          {open ? 'Ocultar avance' : 'Ver avance paso a paso'}
        </button>
        <button type="button" onClick={follow} data-testid={`bill-follow-btn-${id}`} className={`flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] transition-colors ${followed ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-slate-600 text-slate-300 hover:border-emerald-500/50'}`}>
          {followed ? <BellRing className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}{followed ? 'Siguiendo' : 'Seguir proyecto'}
        </button>
      </div>
      {open && (
        <div data-testid={`bill-progress-${id}`} className="mt-3 border-l border-slate-700 pl-4 space-y-3" onClick={(e) => e.preventDefault()}>
          {busy && <Loader2 className="h-4 w-4 animate-spin text-amber-300" />}
          {data?.error && <p className="text-xs text-red-300">No se pudo leer el avance oficial.</p>}
          {data?.steps?.map((s, i) => (
            <div key={i} className="relative" data-testid={`bill-step-${id}-${i}`}>
              <span className="absolute -left-[22px] top-0.5 bg-slate-900">
                {s.official || s.done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <Circle className="h-3.5 w-3.5 text-slate-600" />}
              </span>
              <p className="text-xs text-white font-medium">{s.stage}{s.date ? <span className="text-slate-400 font-normal"> · {fmtDate(s.date)}</span> : null}
                <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${s.official ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-200'}`}>{s.official ? 'Oficial' : 'Estimado · no oficial'}</span>
              </p>
              {s.detail && <p className="text-[11px] text-slate-400 line-clamp-2">{s.detail}</p>}
              {s.files?.length > 0 && <p className="text-[11px] text-slate-500 flex items-center gap-1"><FileText className="h-3 w-3" />{s.files.map((f) => f.name).join(' · ')}</p>}
            </div>
          ))}
          {data?.source && <p className="text-[10px] text-slate-500">Fuente: {data.source}</p>}
        </div>
      )}
    </div>
  );
};
